import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config';
import { useSearchParams } from 'react-router-dom';
import { safeParseDate } from '../utils/dateUtils';
import GIAIReportSelectionHeader from '../components/GI/GIAIReportSelectionHeader';
import GICampaignsSelector from '../components/GI/GICampaignsSelector';
import GIAIAnalysisModal from '../components/GI/GIAIAnalysisModal';
import GIAICustomPromptModal from '../components/GI/GIAICustomPromptModal';
import SortableTable from '../components/SortableTable';
import useAnalytics from '../hooks/useAnalytics';
import { getAdminBadge } from '../utils/getAdminBadge';
import { GI_AI_MAX_SELECTED_CAMPAIGNS } from '../utils/giAiCustomPromptGuardrails';

const DEFAULT_GI_AI_REPORT_CONFIG = { allow_custom_prompt: false };

function GIAIReportViewer({ fetchWithAuth, currentUser }) {
  const [searchParams, setSearchParams] = useSearchParams();

  useAnalytics({ currentUser, module: 'GI_AI_REPORT_VIEWER' });

  // State pour les données et sélections
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // State pour les réponses LongText
  const [longTextData, setLongTextData] = useState(null);
  const [longTextLoading, setLongTextLoading] = useState(false);
  const [longTextError, setLongTextError] = useState(null);

  // État pour la modale d'analyse IA
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [selectedQuestionForAnalysis, setSelectedQuestionForAnalysis] = useState(null);
  const [selectedQuestionResponses, setSelectedQuestionResponses] = useState([]);

  // State for the custom prompt modal
  const [isCustomPromptModalOpen, setIsCustomPromptModalOpen] = useState(false);
  const [viewerConfig, setViewerConfig] = useState(DEFAULT_GI_AI_REPORT_CONFIG);
  const [campaignSelectionMessage, setCampaignSelectionMessage] = useState('');

  // Ref pour le debounce
  const debounceTimeoutRef = useRef(null);

  const { track } = useAnalytics({ currentUser, project: selectedClient?.label });
  const isAdmin = currentUser?.isadmin || currentUser?.issuperadmin;
  const canUseCustomPrompt = isAdmin || viewerConfig.allow_custom_prompt === true;
  const customPromptIsAdminOnly = !viewerConfig.allow_custom_prompt;

  // Initialiser les dates depuis l'URL ou utiliser les valeurs par défaut
  useEffect(() => {
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');

    if (urlStartDate && urlEndDate) {
      setStartDate(safeParseDate(urlStartDate));
      setEndDate(safeParseDate(urlEndDate));
      return;
    }

    const currentYear = new Date().getFullYear();
    const defaultStartDate = new Date(currentYear, 0, 1);
    const defaultEndDate = new Date(currentYear, 11, 31, 23, 59, 59, 999); // Fin de journée du 31 décembre

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('startDate', defaultStartDate.toISOString().split('T')[0]);
    newSearchParams.set('endDate', defaultEndDate.toISOString().split('T')[0]);

    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);

    setSearchParams(newSearchParams);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler pour les changements de plage de dates
  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setSelectedCampaigns([]);
    setCampaignSelectionMessage('');

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('startDate', newStartDate.toISOString().split('T')[0]);
    newSearchParams.set('endDate', newEndDate.toISOString().split('T')[0]);
    newSearchParams.delete('campaigns');
    setSearchParams(newSearchParams);
  };

  // Récupération des clients au chargement
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(`${API_BASE_URL}/api/dwh/gi/clients`);

        if (response.ok) {
          const data = await response.json();
          const formattedClients = data.clients.map(client => ({
            value: client.Id,
            label: client.Name
          }));
          setClients(formattedClients);

          // Vérifier si l'ID de client est dans l'URL
          const clientId = searchParams.get('clientId');
          if (clientId && formattedClients.length > 0) {
            const clientFromUrl = formattedClients.find(c => c.value.toString() === clientId);
            if (clientFromUrl) {
              setSelectedClient(clientFromUrl);
            } else {
              // Si le client dans l'URL n'existe pas, le supprimer de l'URL
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.delete('clientId');
              setSearchParams(newSearchParams);
            }
          }
        } else {
          console.error("Failed to fetch clients");
          setClients([]);
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [fetchWithAuth, searchParams, setSearchParams]);

  // Handler pour sélectionner un client
  const handleClientChange = (client) => {
    setSelectedClient(client);
    setSelectedCampaigns([]);
    setCampaigns([]);
    setCampaignSelectionMessage('');

    const newSearchParams = new URLSearchParams(searchParams);
    if (client) {
      newSearchParams.set('clientId', client.value);
    } else {
      newSearchParams.delete('clientId');
    }
    setSearchParams(newSearchParams);

    if (client) {
      track('gi_ai_report_viewer_select_client', {
        gi_client: client.label
      });
    }
  };

  // Récupération des campagnes quand le client ou les dates changent
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!selectedClient || !startDate || !endDate) {
        setCampaigns([]);
        setSelectedCampaigns([]);
        return;
      }

      try {
        setCampaignsLoading(true);
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/gi/ai-report/campaigns?startDate=${formattedStartDate}&endDate=${formattedEndDate}&clientId=${selectedClient.value}`
        );

        if (response.ok) {
          const data = await response.json();
          const formattedCampaigns = data.data.map(campaign => ({
            value: campaign.CampaignId,
            label: campaign.CampaignName,
            data: campaign
          })).sort((a, b) => new Date(a.data.CampaignStartDate) - new Date(b.data.CampaignStartDate));

          setCampaigns(formattedCampaigns);
        } else {
          console.error("Failed to fetch campaigns");
          setCampaigns([]);
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        setCampaigns([]);
      } finally {
        setCampaignsLoading(false);
      }
    };

    fetchCampaigns();
  }, [selectedClient, startDate, endDate, fetchWithAuth]);

  useEffect(() => {
    const fetchViewerConfig = async () => {
      if (!selectedClient) {
        setViewerConfig(DEFAULT_GI_AI_REPORT_CONFIG);
        return;
      }

      setViewerConfig(DEFAULT_GI_AI_REPORT_CONFIG);

      try {
        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/gi/ai-report/config?clientId=${selectedClient.value}`
        );

        if (response.ok) {
          const configData = await response.json();
          setViewerConfig(configData || DEFAULT_GI_AI_REPORT_CONFIG);
        } else {
          setViewerConfig(DEFAULT_GI_AI_REPORT_CONFIG);
        }
      } catch (error) {
        console.error('Error fetching GI AI report config:', error);
        setViewerConfig(DEFAULT_GI_AI_REPORT_CONFIG);
      }
    };

    fetchViewerConfig();
  }, [selectedClient, fetchWithAuth]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handler pour sélectionner des campagnes
  const handleCampaignsChange = (campaigns) => {
    setCampaignSelectionMessage('');
    setSelectedCampaigns(campaigns || []);
    // Réinitialiser les données LongText quand les campagnes changent
    setLongTextData(null);
    setLongTextError(null);

    if (campaigns && campaigns.length > 0) {
      track('gi_ai_report_viewer_select_campaigns', {
        gi_campaigns_count: campaigns.length,
        gi_client: selectedClient?.label
      });
    }
  };

  // Fonction debouncée pour récupérer les réponses LongText
  const debouncedFetchLongTextResponses = useCallback(
    (campaigns, start, end) => {
      // Nettoyer le timeout précédent
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Si aucune campagne sélectionnée, vider immédiatement
      if (!campaigns || campaigns.length === 0 || !start || !end) {
        setLongTextData(null);
        setLongTextLoading(false);
        return;
      }

      // Indiquer qu'une nouvelle recherche va commencer
      setLongTextLoading(true);
      setLongTextError(null);

      // Créer un nouveau timeout
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const formattedStartDate = start.toISOString().split('T')[0];
          const formattedEndDate = end.toISOString().split('T')[0];
          const campaignIds = campaigns.map(c => c.value).join(',');

          const response = await fetchWithAuth(
            `${API_BASE_URL}/api/dwh/gi/ai-report/longtext-responses?startDate=${formattedStartDate}&endDate=${formattedEndDate}&campaignIds=${campaignIds}`
          );

          if (response.ok) {
            const data = await response.json();
            setLongTextData(data);
          } else {
            throw new Error(`Erreur ${response.status}: ${response.statusText}`);
          }
        } catch (error) {
          console.error("Error fetching longtext responses:", error);
          setLongTextError(error.message || "Erreur lors de la récupération des données");
        } finally {
          setLongTextLoading(false);
        }
      }, 1000); // Debounce de 1s
    },
    [fetchWithAuth]
  );

  // Récupération des réponses LongText quand les campagnes sont sélectionnées
  useEffect(() => {
    debouncedFetchLongTextResponses(selectedCampaigns, startDate, endDate);

    // Cleanup function pour nettoyer le timeout si le composant est démonté
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [selectedCampaigns, startDate, endDate, debouncedFetchLongTextResponses]);

  // Regroupement des questions par texte identique
  const groupedQuestions = useMemo(() => {
    if (!longTextData || !longTextData.data.questions) {
      return [];
    }

    const questionsMap = new Map();

    // Grouper les questions par texte trimé
    longTextData.data.questions.forEach(question => {
      const trimmedText = question.questionText.trim();

      if (!questionsMap.has(trimmedText)) {
        questionsMap.set(trimmedText, {
          questionText: trimmedText,
          surveys: new Set(),
          totalResponses: 0,
          questions: []
        });
      }

      const group = questionsMap.get(trimmedText);
      group.surveys.add(question.surveyName);
      group.questions.push(question);
      group.totalResponses += question.answers_count; // Additionner les counts
    });

    // Convertir en array et formatter
    return Array.from(questionsMap.entries()).map(([text, data]) => ({
      questionText: text,
      surveyNames: Array.from(data.surveys).sort(),
      totalResponses: data.totalResponses,
      questionsCount: data.questions.length,
      surveys: data.surveys.size,
      surveyDetails: data.questions.map(q => ({ // Détails des surveys pour la modale
        questionId: q.questionId,
        surveyId: q.surveyId,
        surveyName: q.surveyName,
        campaignId: q.campaignId,
        campaignName: q.campaignName,
        answers_count: q.answers_count
      }))
    })).sort((a, b) => b.totalResponses - a.totalResponses); // Trier par nombre de réponses décroissant
  }, [longTextData]);

  // Handler pour ouvrir la modale d'analyse IA
  const handleQuestionClick = (questionData) => {
    setSelectedQuestionForAnalysis(questionData);
    setSelectedQuestionResponses([]); // Plus de réponses détaillées disponibles
    setIsAnalysisModalOpen(true);

    track('gi_ai_report_viewer_analyze_question', {
      gi_question_text: questionData.questionText.substring(0, 100),
      gi_responses_count: questionData.totalResponses,
      gi_surveys_count: questionData.surveys,
      gi_client: selectedClient?.label
    });
  };

  // Handler pour fermer la modale d'analyse IA
  const handleCloseAnalysisModal = () => {
    setIsAnalysisModalOpen(false);
    setSelectedQuestionForAnalysis(null);
    setSelectedQuestionResponses([]);
  };

  const handleOpenCustomPromptModal = () => {
    setIsCustomPromptModalOpen(true);
    setCampaignSelectionMessage('');
    track('gi_ai_report_viewer_open_custom_prompt', {
      gi_client: selectedClient?.label
    });
  };

  const handleCloseCustomPromptModal = () => {
    setIsCustomPromptModalOpen(false);
  };

  const handleCampaignLimitReached = (limit) => {
    setCampaignSelectionMessage(`Vous pouvez sélectionner jusqu'à ${limit} campagnes maximum.`);
  };

  const questionsColumns = [
    {
      key: 'questionText',
      label: 'Question',
      render: (item) => (
        <div className="max-w-md">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-3">
            {item.questionText}
          </div>
        </div>
      )
    },
    {
      key: 'surveyNames',
      label: 'Formulaires',
      render: (item) => (
        <div className="max-w-xs">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {item.surveys} formulaire{item.surveys > 1 ? 's' : ''}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-500 line-clamp-2">
            {item.surveyNames.join(', ')}
          </div>
        </div>
      )
    },
    {
      key: 'totalResponses',
      label: 'Réponses',
      render: (item) => (
        <div className="text-center">
          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {item.totalResponses}
          </div>
        </div>
      ),
      rawValue: (item) => item.totalResponses
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <div className="text-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleQuestionClick(item);
            }}
            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            Analyser
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="container mx-auto p-4 relative">
      {/* Header avec les sélections */}
      <GIAIReportSelectionHeader
        clients={clients}
        selectedClient={selectedClient}
        setSelectedClient={handleClientChange}
        startDate={startDate}
        endDate={endDate}
        onDatesChange={handleDateRangeChange}
        loading={loading}
      />

      {/* Indicateur de chargement */}
      {loading && (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* État initial - aucun client sélectionné */}
      {!selectedClient && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="text-center py-10 text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <h3 className="text-lg font-medium mb-2">Sélectionnez un client</h3>
            <p>Choisissez un client pour afficher ses campagnes et commencer l'analyse.</p>
          </div>
        </div>
      )}

      {/* Client sélectionné mais pas de campagnes */}
      {selectedClient && !campaignsLoading && campaigns.length === 0 && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="text-center py-10 text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium mb-2">Aucune campagne trouvée</h3>
            <p>Aucune campagne n'a été trouvée pour ce client sur la période sélectionnée.</p>
          </div>
        </div>
      )}

      {/* Layout principal à 2 colonnes - uniquement si client sélectionné et campagnes disponibles */}
      {selectedClient && !loading && campaigns.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ height: 'calc(100vh - 240px)' }}>
          {/* Colonne gauche - Sélection des campagnes (1/3) */}
          <div className="lg:col-span-1" style={{ height: 'calc(100vh - 240px)' }}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 h-full flex flex-col">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex-shrink-0">
                Campagnes ({selectedCampaigns.length}/{GI_AI_MAX_SELECTED_CAMPAIGNS})
              </h2>

              {campaignSelectionMessage ? (
                <div className="mb-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-700 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-200">
                  {campaignSelectionMessage}
                </div>
              ) : null}

              <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
                <GICampaignsSelector
                  selectedClient={selectedClient}
                  campaigns={campaigns}
                  campaignsLoading={campaignsLoading}
                  selectedCampaigns={selectedCampaigns}
                  onCampaignsChange={handleCampaignsChange}
                  maxSelectedCampaigns={GI_AI_MAX_SELECTED_CAMPAIGNS}
                  onLimitReached={handleCampaignLimitReached}
                  compactMode={true}
                />
              </div>
            </div>
          </div>

          {/* Colonne droite - Tableau des questions (2/3) */}
          <div className="lg:col-span-2" style={{ height: 'calc(100vh - 240px)' }}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 h-full flex flex-col">
              {/* Indicateur de chargement des données LongText */}
              {longTextLoading && selectedCampaigns.length > 0 && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Chargement des questions...</span>
                  </div>
                </div>
              )}

              {/* Affichage des questions regroupées */}
              {!longTextLoading && longTextData && groupedQuestions.length > 0 && (
                <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>
                  {/* En-tête avec statistiques */}
                  <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 py-1 px-3 rounded-full text-sm">
                          {selectedCampaigns.length}/{GI_AI_MAX_SELECTED_CAMPAIGNS} campagne{selectedCampaigns.length > 1 ? 's' : ''}
                        </div>
                        <div className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 py-1 px-3 rounded-full text-sm">
                          {groupedQuestions.length} question{groupedQuestions.length > 1 ? 's' : ''}
                        </div>
                        <div className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 py-1 px-3 rounded-full text-sm">
                          {groupedQuestions.reduce((sum, q) => sum + q.totalResponses, 0)} réponse{groupedQuestions.reduce((sum, q) => sum + q.totalResponses, 0) > 1 ? 's' : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {startDate.toLocaleDateString('fr-FR')} - {endDate.toLocaleDateString('fr-FR')}
                        </div>

                        {canUseCustomPrompt && (
                          <>
                            {customPromptIsAdminOnly && isAdmin && getAdminBadge()}
                            <button
                              onClick={handleOpenCustomPromptModal}
                              disabled={groupedQuestions.length === 0}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                groupedQuestions.length === 0
                                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                              title="Run your own prompt on selected open questions"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                              </svg>
                              Custom prompt
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tableau avec hauteur flexible et scroll interne */}
                  <div className="flex-1" style={{ minHeight: 0 }}>
                    <SortableTable
                      data={groupedQuestions}
                      columns={questionsColumns}
                      emptyMessage="Aucune question trouvée"
                      exportable={true}
                      exportFilename="questions_ia_report.csv"
                      globalSearch={true}
                      onRowClick={handleQuestionClick}
                      fullHeight={true}
                    />
                  </div>
                </div>
              )}

              {/* Messages d'état */}
              {!longTextLoading && selectedCampaigns.length === 0 && (
                <div className="flex-1 flex items-center justify-center text-center text-gray-500 dark:text-gray-400">
                  <div>
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p>Sélectionnez une ou plusieurs campagnes pour voir les questions d'analyse</p>
                  </div>
                </div>
              )}

              {!longTextLoading && selectedCampaigns.length > 0 && longTextData && groupedQuestions.length === 0 && (
                <div className="flex-1 flex items-center justify-center text-center text-gray-500 dark:text-gray-400">
                  <div>
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>Aucune question longue trouvée pour les campagnes sélectionnées</p>
                  </div>
                </div>
              )}

              {!longTextLoading && longTextError && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="max-w-md rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
                    {longTextError}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modale d'analyse IA */}
      {isAnalysisModalOpen && selectedQuestionForAnalysis && (
        <GIAIAnalysisModal
          isOpen={isAnalysisModalOpen}
          onClose={handleCloseAnalysisModal}
          clientId={selectedClient?.value}
          questionData={{
            questionText: selectedQuestionForAnalysis.questionText,
            surveys: selectedQuestionForAnalysis.surveyDetails,
            surveyDetails: selectedQuestionForAnalysis.surveyDetails
          }}
          responses={selectedQuestionResponses}
          fetchWithAuth={fetchWithAuth}
          startDate={startDate}
          endDate={endDate}
        />
      )}

      {isCustomPromptModalOpen && (
        <GIAICustomPromptModal
          isOpen={isCustomPromptModalOpen}
          onClose={handleCloseCustomPromptModal}
          groupedQuestions={groupedQuestions}
          selectedCampaigns={selectedCampaigns}
          fetchWithAuth={fetchWithAuth}
          clientId={selectedClient?.value}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </div>
  );
}

export default GIAIReportViewer;